"use client"

import { usePage } from "@inertiajs/react"
import {
  BookOpen01,
  Edit02,
  ImageX,
  LinkExternal01,
  Lock01,
  PlayCircle,
  Trash01,
} from "@untitledui/icons"
import type { DragEvent } from "react"

import { Badge } from "@/components/ui/badge"
import { ButtonUtility } from "@/components/ui/button-utility"
import { Dropdown } from "@/components/ui/dropdown-menu"
import { Link } from "@/components/ui/link"
import { ShadowWrapper } from "@/components/ui/shadow-wrapper"
import { TagBadgeGroup } from "@/components/ui/tag-badge"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { formatDuration } from "@/lib/utils/"
import type { Playlist, SharedData, Track } from "@/types"

import SelectMyPlaylistModal from "./select-my-playlist-modal"

export interface TrackCardProps {
  track: Track
  position: number
  playlist?: Playlist
  showActions?: boolean
  onEdit?: (track: Track) => void
  onDelete?: (track: Track) => void
  onClickTitle?: (track: Track) => void
  onClickThumbnail?: (position: number) => void
  playlistRepository?: Playlist[]
  loadingPlaylistRepository?: boolean
  draggable?: boolean
  onDragStart?: (event: DragEvent<HTMLDivElement>, track: Track) => void
  onDragOver?: (event: DragEvent<HTMLDivElement>, track: Track) => void
  onDrop?: (event: DragEvent<HTMLDivElement>, track: Track) => void
}

/**
 * Track Card component.
 * @param track - The track.
 * @param position - The position of the track.
 * @param showActions - Whether to show the actions.
 * @param onEdit - The function to call when the edit button is clicked.
 * @param onDelete - The function to call when the delete button is clicked.
 * @param playlistRepository - User's playlist repository.
 * @returns The Track Card component.
 */
export function TrackCard({
  track,
  position,
  playlist,
  showActions = true,
  onEdit,
  onDelete,
  onClickTitle,
  onClickThumbnail,
  playlistRepository = [],
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
}: TrackCardProps) {
  const { visitor } = usePage<SharedData>().props
  const isMobile = useIsMobile()
  const { post } = track
  if (!post) return null
  const contentType = post.content_body?.type
  const isPlayable = post.content_body?.playable
  const isDisabled = !post.published && !track.is_owner

  const duration = formatDuration(
    post.content_body?.duration_seconds ?? undefined,
  )
  const handleOpenNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  const styles = {
    dragCursor: draggable && "cursor-grab active:cursor-grabbing",
    disabledBg: isDisabled && "bg-stone-100",
    disabledLink: isDisabled && "hover:cursor-not-allowed",
  }

  return (
    <div
      className={cn("group relative", styles.dragCursor)}
      draggable={draggable}
      onDragStart={(event) => onDragStart?.(event, track)}
      onDragOver={(event) => onDragOver?.(event, track)}
      onDrop={(event) => onDrop?.(event, track)}
    >
      <ShadowWrapper>
        <div
          className={cn(
            "border-secondary bg-primary relative z-1 flex gap-4 rounded-xl border p-4 transition-all duration-200 group-hover:-translate-y-0.5 md:h-30",
            styles.disabledBg,
          )}
        >
          {/* Index (track's position) */}
          <div className="h-full w-2">
            <span className="text-quaternary text-lg font-semibold md:text-xl">
              {position}
            </span>
          </div>

          {/* Thumbnail */}
          <div
            role="button"
            onClick={() => !isDisabled && onClickThumbnail?.(position)}
            className={`group/thumb bg-secondary_subtle relative size-16 shrink-0 self-center overflow-hidden rounded-lg hover:cursor-pointer md:size-20 ${styles.disabledLink}`}
          >
            {post.thumbnail_url ? (
              <img
                src={post.thumbnail_url}
                alt={post.title}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center bg-gray-500">
                <ImageX className="size-8 object-cover text-white/30" />
              </div>
            )}
            <div
              className={`absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-500 ease-out group-hover/thumb:opacity-100 ${styles.disabledLink}`}
            >
              {isDisabled ? (
                <Lock01 className="size-8 text-white" />
              ) : isPlayable ? (
                <PlayCircle className="size-8 text-white" />
              ) : (
                <BookOpen01 className="size-8 text-white" />
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
            <div className="flex items-center gap-2">
              <Link
                onClick={() => !isDisabled && onClickTitle?.(track)}
                className={`text-md text-primary hover:text-primary/80 truncate font-semibold transition ${styles.disabledLink}`}
              >
                {post.title}
              </Link>
            </div>

            <div className="flex items-center gap-2">
              {post.creator && (
                <span className="text-tertiary truncate text-sm">
                  {post.creator}
                </span>
              )}
              {contentType && (
                <Badge size="sm" color="gray" type="modern">
                  {contentType.replace("Body", "")}
                </Badge>
              )}
              {duration && (
                <span className="text-quaternary text-sm">{duration}</span>
              )}
            </div>

            <TagBadgeGroup tags={track.tags} variant="row" />

            <div className="hidden text-sm md:block">
              {track.notes && (
                <p className="text-primary/80 line-clamp-2 text-sm">
                  {track.notes.replace(/<[^>]*>/g, "")}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center">
            <div className="flex shrink-0 items-center gap-1">
              {playlist && visitor.authenticated && (
                <SelectMyPlaylistModal
                  playlists={playlistRepository}
                  playlist={playlist}
                  track={track}
                />
              )}
              {/* Actions dropdown (mobile) */}
              {isMobile ? (
                <Dropdown.Root>
                  <Dropdown.DotsButton />
                  <Dropdown.Popover>
                    <Dropdown.Menu>
                      {post.canonical_url && (
                        <Dropdown.Item
                          id="origin"
                          label="Visit original source"
                          icon={LinkExternal01}
                          onAction={() => handleOpenNewTab(post.canonical_url!)}
                        />
                      )}
                      {showActions && (
                        <>
                          {onEdit && track.is_owner && (
                            <Dropdown.Item
                              id="edit"
                              label="Edit"
                              icon={Edit02}
                              onAction={() => onEdit(track)}
                            />
                          )}
                          {onDelete && (
                            <Dropdown.Item
                              id="remove"
                              label="Remove"
                              icon={Trash01}
                              onAction={() => onDelete(track)}
                            />
                          )}
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.Root>
              ) : (
                <>
                  {/* Actions buttons (desktop) */}
                  {post.canonical_url && (
                    <ButtonUtility
                      id="origin"
                      size="xs"
                      color="tertiary"
                      tooltip="Visit original source"
                      icon={LinkExternal01}
                      onClick={() => handleOpenNewTab(post.canonical_url!)}
                    />
                  )}
                  {showActions && (
                    <>
                      {onEdit && track.is_owner && (
                        <ButtonUtility
                          id="edit"
                          size="xs"
                          color="tertiary"
                          tooltip="Edit"
                          icon={Edit02}
                          onClick={() => onEdit(track)}
                        />
                      )}
                      {onDelete && (
                        <ButtonUtility
                          id="remove"
                          size="xs"
                          color="tertiary"
                          tooltip="Remove"
                          icon={Trash01}
                          onClick={() => onDelete(track)}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ShadowWrapper>
    </div>
  )
}
